import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import UserDrawer from "./UserDrawer";
import { RoleChip, Skeleton, StatusChip, formatChf, formatDate, formatDateTime, formatRelative } from "./adminUi";

export const FILTERS = [
  { key: "all",           label: "All",            test: () => true },
  { key: "active",        label: "Active",         test: (u) => u.status === "active" || u.status === "ending" },
  { key: "ending",        label: "Renews soon",    test: (u) => u.status === "ending" },
  { key: "complimentary", label: "Complimentary",  test: (u) => u.status === "complimentary" },
  { key: "expired",       label: "Expired",        test: (u) => u.status === "expired" },
  { key: "never",         label: "Never paid",     test: (u) => u.status === "never" },
  { key: "autotrading",   label: "Auto trading",   test: (u) => u.autoTrading },
  { key: "managers",      label: "Portfolio managers", test: (u) => u.role === "portfolio_manager" },
  { key: "admins",        label: "Admins",         test: (u) => u.role === "admin" },
];

const STATUS_ORDER = { active: 0, ending: 1, complimentary: 2, expired: 3, never: 4 };
const COLUMNS = [
  { key: "name",        label: "User",         value: (u) => (u.name || u.email || "").toLowerCase() },
  { key: "status",      label: "Status",       value: (u) => STATUS_ORDER[u.status] ?? 9 },
  { key: "paidUntil",   label: "Paid until",   value: (u) => (u.paidUntil ? Date.parse(u.paidUntil) : 0) },
  { key: "lastLoginAt", label: "Last login",   value: (u) => (u.lastLoginAt ? Date.parse(u.lastLoginAt) : 0) },
  { key: "logins30d",   label: "Logins 30 d",  value: (u) => u.logins30d, numeric: true },
  { key: "totalPaid",   label: "Total paid",   value: (u) => u.totalPaid, numeric: true },
  { key: "autoTrading", label: "Auto trading", value: (u) => (u.autoTrading ? 1 : 0) },
  { key: "createdAt",   label: "Signed up",    value: (u) => (u.createdAt ? Date.parse(u.createdAt) : 0) },
];

const AdminUsersTab = ({ auth }) => {
  const [params, setParams] = useSearchParams();
  const [data, setData]           = useState(null); // { users, stripeCustomerUrl }
  const [error, setError]         = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery]         = useState("");
  const [sort, setSort]           = useState({ key: "createdAt", dir: -1 });

  const filter = FILTERS.some((f) => f.key === params.get("filter")) ? params.get("filter") : "all";
  const openUserId = params.get("user");

  const setParam = (name, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value); else next.delete(name);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setError(null);
      try {
        const res = await fetch("/api/admin/users", {
          headers: { Authorization: "Bearer " + auth.token },
          signal: controller.signal,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
        setData(body);
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      }
    };
    load();
    return () => controller.abort();
  }, [auth.token, reloadKey]);

  const users = useMemo(() => data?.users || [], [data]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, users.filter(f.test).length])), [users]);

  const rows = useMemo(() => {
    const test = FILTERS.find((f) => f.key === filter).test;
    const q = query.trim().toLowerCase();
    const column = COLUMNS.find((c) => c.key === sort.key);
    return users
      .filter(test)
      .filter((u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q))
      .sort((a, b) => {
        const x = column.value(a), y = column.value(b);
        return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
      });
  }, [users, filter, query, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === "name" || key === "status" ? 1 : -1 }));

  const openUser = users.find((u) => u.id === openUserId) || null;

  return (
    <section aria-label="Users">
      <div className="adm-toolbar">
        <input
          type="search" className="adm-search" placeholder="Search name or email"
          value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search users"
        />
        <div className="adm-filters" role="group" aria-label="Filter users">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" aria-pressed={filter === f.key}
                    className={`adm-filter ${filter === f.key ? "is-active" : ""}`}
                    onClick={() => setParam("filter", f.key === "all" ? null : f.key)}>
              {f.label} <span>{data ? counts[f.key] : ""}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="adm-panel adm-panel--flush">
        {error && (
          <div className="adm-message" role="alert">
            <p>Couldn't load the users: {error}</p>
            <button type="button" className="adm-btn adm-btn--outline" onClick={() => setReloadKey((n) => n + 1)}>Try again</button>
          </div>
        )}
        {!error && !data && <div aria-busy="true" className="adm-table-skeleton">{[0, 1, 2].map((i) => <Skeleton key={i} className="adm-skeleton--row" />)}</div>}
        {!error && data && (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={c.numeric ? "is-numeric" : ""}
                        aria-sort={sort.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
                      <button type="button" onClick={() => toggleSort(c.key)}>
                        {c.label}{sort.key === c.key ? (sort.dir === 1 ? " ↑" : " ↓") : ""}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={COLUMNS.length} className="adm-empty">No users match.</td></tr>
                )}
                {rows.map((u) => (
                  <tr key={u.id} tabIndex={0} onClick={() => setParam("user", u.id)}
                      onKeyDown={(e) => { if (e.key === "Enter") setParam("user", u.id); }}>
                    <td>
                      <span className="adm-user__name">{u.name}<RoleChip role={u.role} /></span>
                      <span className="adm-user__email">{u.email}</span>
                    </td>
                    <td><StatusChip status={u.status} /></td>
                    <td>{formatDate(u.paidUntil)}</td>
                    <td title={formatDateTime(u.lastLoginAt)}>{formatRelative(u.lastLoginAt)}</td>
                    <td className="is-numeric">
                      {u.logins30d}
                      {u.failedLogins30d > 0 && <span className="adm-text-danger" title="Failed logins in 30 days"> · {u.failedLogins30d} failed</span>}
                    </td>
                    <td className="is-numeric">{u.totalPaid ? formatChf(u.totalPaid) : "—"}</td>
                    <td>
                      {u.autoTrading
                        ? <span className="adm-on">On · {(u.tradedAccounts || []).map((m) => (m === "live" ? "Live" : "Paper")).join(" + ") || "no account"}</span>
                        : <span className="adm-off">Off</span>}
                    </td>
                    <td>{formatDate(u.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openUser && (
        <UserDrawer
          auth={auth}
          user={openUser}
          stripeCustomerUrl={data.stripeCustomerUrl}
          onClose={() => setParam("user", null)}
          onChanged={() => setReloadKey((n) => n + 1)}
        />
      )}
    </section>
  );
};

export default AdminUsersTab;
